/**
 * Fase 28 · Formato unificado de fecha/hora en zona horaria GMT-4 (Caracas).
 *
 * Todos los `iso` que recibimos del backend vienen en UTC. Aquí los
 * convertimos consistentemente a GMT-4 (offset fijo −240 min) mostrando el
 * huso "GMT-4" al usuario cuando lo pide.
 *
 * Notas:
 * - Se usa offset fijo (no DST) — es el estándar de Venezuela desde 2016.
 * - Si el ISO no trae zona (ej. "2025-01-01T10:00:00"), se asume UTC.
 */

const TIMEZONE_LABEL = "GMT-4";
const OFFSET_MINUTES = -240; // GMT-4

function toGmtMinus4(iso) {
  if (!iso) return null;
  try {
    // Si no trae 'Z' ni offset, asumir UTC
    const src = /[Zz]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
    const d = new Date(src);
    if (isNaN(d.getTime())) return null;
    // Ajustar al offset objetivo. getTime() es UTC → sumamos offset target.
    return new Date(d.getTime() + OFFSET_MINUTES * 60 * 1000);
  } catch {
    return null;
  }
}

function pad(n) { return String(n).padStart(2, "0"); }

/**
 * Fecha larga y hora HH:MM. Ejemplo: "13 feb 2026, 14:37 (GMT-4)".
 */
export function formatDateTime(iso, { withTz = true } = {}) {
  const d = toGmtMinus4(iso);
  if (!d) return "—";
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  const day = d.getUTCDate();
  const month = months[d.getUTCMonth()];
  const year = d.getUTCFullYear();
  const hh = pad(d.getUTCHours());
  const mm = pad(d.getUTCMinutes());
  const base = `${day} ${month} ${year}, ${hh}:${mm}`;
  return withTz ? `${base} (${TIMEZONE_LABEL})` : base;
}

/** Solo HH:MM en GMT-4 — útil para chat compactos. */
export function formatTime(iso) {
  const d = toGmtMinus4(iso);
  if (!d) return "—";
  return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
}

/** Fecha corta "13 feb 2026" en GMT-4. */
export function formatDate(iso) {
  const d = toGmtMinus4(iso);
  if (!d) return "—";
  const months = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Fecha corta con hora, sin badge de GMT. Para chats/tablas densas. */
export function formatDateTimeShort(iso) {
  return formatDateTime(iso, { withTz: false });
}

export const TZ_LABEL = TIMEZONE_LABEL;
